import { round } from 'lodash'
import { i18n } from '../i18n'
import {
  HourSerieCalculatorResult,
  InputHourSerieType,
} from '../building-energy-model/hour-series-calculator'
import {
  BuildingEnergyModelFragment,
  ConsumptionEnergyType,
  CoolingParameters,
  EnergySystemFragment,
  EnergySystemType,
  HourSeriesType,
  ScheduleFragment,
  VentilationDeviceData,
  VentilationDeviceType,
  VentilationParameters,
} from '../generated/graphql'
import { calculateHourSerieFactors } from '../schedule/hour-series-factors-calculator'
import { hoursInYear } from '../util/hours-in-year'

type DeviceResult = {
  ahuHeating: number[]
  exhaustAirHeatPumpPotential: number[]
  fanElectricityConsumption: number[]
  ahuCoolingDemand: number[]
  supplyAirSpaceHeatDemand: number[]
}

const airDensity = (outsideTemp: number) =>
  round(-0.0048 * outsideTemp + 1.2966, 2)

const deviceCalculator = (
  device: VentilationDeviceData,
  heatPumpExhaustAirMinimumTemp: number,
  scheduleSerie: number[],
  outsideTempHourSerie: number[],
  coolingDemandDisplacementConstant: number,
): DeviceResult => {
  const ahuHeatingRadiatorSerie = Array.from(
    Array(hoursInYear).fill(0),
  ) as number[]
  const exhaustAirHeatPumpSerie = Array.from(
    Array(hoursInYear).fill(0),
  ) as number[]
  const fanConsumptionSerie = Array.from(Array(hoursInYear).fill(0)) as number[]
  const ahuCoolingSerie = Array.from(Array(hoursInYear).fill(0)) as number[]
  const spaceHeatSerie = Array.from(Array(hoursInYear).fill(0)) as number[]

  for (let i = 0; i < outsideTempHourSerie.length; i++) {
    const currTemp = outsideTempHourSerie[i]

    const chilldropFactor =
      currTemp < device.chilldropTempLimit ? 1 - device.chilldrop : 1

    const operatingPower =
      scheduleSerie[i] * device.demandBasedUsageCoefficient * chilldropFactor

    const airFlowMass = round(
      device.airFlow * airDensity(currTemp) * operatingPower,
      3,
    )

    let totalAhuHeatingDemand = round(
      airFlowMass * (device.indoorAirTemp - currTemp),
      2,
    )
    if (totalAhuHeatingDemand < 0) totalAhuHeatingDemand = 0

    let heatRecoveryHeatingPower = 0
    if (device.systemType !== VentilationDeviceType.Exhaust) {
      heatRecoveryHeatingPower = round(
        airFlowMass *
          device.heatRecoveryEfficiency *
          (device.returnAirTemp - currTemp),
        2,
      )

      const altHeatRecoveryHeatingPower = round(
        airFlowMass * (device.returnAirTemp - device.exhaustAirMinimumTemp),
        2,
      )

      if (heatRecoveryHeatingPower > altHeatRecoveryHeatingPower)
        heatRecoveryHeatingPower = altHeatRecoveryHeatingPower

      if (heatRecoveryHeatingPower < 0) heatRecoveryHeatingPower = 0

      if (heatRecoveryHeatingPower > totalAhuHeatingDemand)
        heatRecoveryHeatingPower = totalAhuHeatingDemand
    }

    let ahuHeatingRadiatorPower = 0
    if (device.systemType !== VentilationDeviceType.Exhaust) {
      ahuHeatingRadiatorPower = round(
        airFlowMass * (device.supplyAirTemp - 1 - currTemp) -
          heatRecoveryHeatingPower,
        2,
      )
      if (ahuHeatingRadiatorPower < 0) ahuHeatingRadiatorPower = 0
    }

    ahuHeatingRadiatorSerie[i] = ahuHeatingRadiatorPower

    // If exhaust air heat pump exists
    if (device.exhaustAirHeatPump === 1) {
      const heatPumpPotential = round(
        airFlowMass * (device.returnAirTemp - heatPumpExhaustAirMinimumTemp) -
          heatRecoveryHeatingPower,
        2,
      )

      if (heatPumpPotential > 0) exhaustAirHeatPumpSerie[i] = heatPumpPotential
    }

    // Fan electricity consumption
    fanConsumptionSerie[i] = round(
      (airFlowMass / airDensity(currTemp)) * device.SFP,
      2,
    )

    // Supply air space heat demand
    spaceHeatSerie[i] = totalAhuHeatingDemand
    if (device.systemType !== VentilationDeviceType.Exhaust) {
      spaceHeatSerie[i] = Math.max(
        0,
        round(airFlowMass * (device.indoorAirTemp - device.supplyAirTemp), 2),
      )
    }

    // AHU cooling demand (if cooling coil exists)
    if (device.coolingCoil === 1) {
      ahuCoolingSerie[i] = Math.max(
        0,
        round(
          airFlowMass *
            (currTemp -
              device.supplyAirTemp -
              coolingDemandDisplacementConstant),
          2,
        ),
      )
    }
  }

  const result: DeviceResult = {
    ahuHeating: ahuHeatingRadiatorSerie,
    exhaustAirHeatPumpPotential: exhaustAirHeatPumpSerie,
    fanElectricityConsumption: fanConsumptionSerie,
    ahuCoolingDemand: ahuCoolingSerie,
    supplyAirSpaceHeatDemand: spaceHeatSerie,
  }
  return result
}

export const ventilationCalculator = (
  schedules: ScheduleFragment[],
  outsideTempHourSerie: number[],
  energySystem: EnergySystemFragment,
  coolingDemandDisplacementConstant: number,
  year: number,
): HourSerieCalculatorResult => {
  const deviceResults: DeviceResult[] = []

  const params = energySystem.parameters as VentilationParameters

  for (let i = 0; i < params.deviceData.length; i++) {
    const device = params.deviceData[i]

    const schedule = schedules.find((s) => s.id === device.deviceScheduleId)
    if (schedule) {
      // TODO: Keep already calculated schedules if used more than once
      const scheduleSerie = calculateHourSerieFactors(schedule, year)

      deviceResults.push(
        deviceCalculator(
          params.deviceData[i],
          params.heatPumpExhaustAirMinimumTemp,
          scheduleSerie,
          outsideTempHourSerie,
          coolingDemandDisplacementConstant,
        ),
      )
    }
  }

  const ahuHeatingSerie = [...deviceResults[0].ahuHeating]
  const exhaustAirHeatPumpSerie = [
    ...deviceResults[0].exhaustAirHeatPumpPotential,
  ]
  const fanConsumptionSerie = [...deviceResults[0].fanElectricityConsumption]
  const ahuCoolingSerie = [...deviceResults[0].ahuCoolingDemand]
  const spaceHeatSerie = [...deviceResults[0].supplyAirSpaceHeatDemand]

  for (let i = 1; i < deviceResults.length; i++) {
    const heatingSerie = deviceResults[i].ahuHeating
    const heatPumpDeviceSerie = deviceResults[i].exhaustAirHeatPumpPotential
    const fanSerie = deviceResults[i].fanElectricityConsumption
    const coolingSerie = deviceResults[i].ahuCoolingDemand
    const spaceSerie = deviceResults[i].supplyAirSpaceHeatDemand

    for (let j = 0; j < heatingSerie.length; j++) {
      ahuHeatingSerie[j] += heatingSerie[j]
      exhaustAirHeatPumpSerie[j] += heatPumpDeviceSerie[j]
      fanConsumptionSerie[j] += fanSerie[j]
      ahuCoolingSerie[j] += coolingSerie[j]
      spaceHeatSerie[j] += spaceSerie[j]
    }
  }

  for (let i = 0; i < ahuHeatingSerie.length; i++) {
    ahuHeatingSerie[i] =
      round(ahuHeatingSerie[i] / params.heatingDistributionEfficiency, 2) * 1000
    exhaustAirHeatPumpSerie[i] *= 1000
  }

  const result: HourSerieCalculatorResult = {
    outputs: [
      {
        hourSerieType: HourSeriesType.HeatingDemand,
        energySystemType: EnergySystemType.Ventilation,
        consumptionEnergyType: ConsumptionEnergyType.VentilationHeating,
        hourSerieName: i18n.t(
          'buildingEnergyModels.hourSeriesNames.ventilationHeating',
        ),
        hourSerie: ahuHeatingSerie,
      },
      {
        hourSerieType: HourSeriesType.WasteHeating,
        energySystemType: EnergySystemType.Ventilation,
        consumptionEnergyType:
          ConsumptionEnergyType.ExhaustAirHeatPumpPotential,
        hourSerieName: i18n.t(
          'buildingEnergyModels.hourSeriesNames.exhaustAirHeatPumpPotential',
        ),
        hourSerie: exhaustAirHeatPumpSerie,
      },
    ],
    inputs: [
      {
        sourceSystemType: EnergySystemType.Ventilation,
        targetSystemType: EnergySystemType.ElectricityConsumption,
        inputSerieType: InputHourSerieType.VentilationElectricityDemand,
        hourSerie: fanConsumptionSerie,
      },
      {
        sourceSystemType: EnergySystemType.Ventilation,
        targetSystemType: EnergySystemType.Cooling,
        inputSerieType: InputHourSerieType.VentilationCoolingDemand,
        hourSerie: ahuCoolingSerie,
      },
      {
        sourceSystemType: EnergySystemType.Ventilation,
        targetSystemType: EnergySystemType.SpaceHeating,
        inputSerieType: InputHourSerieType.VentilationHeatingDemand,
        hourSerie: spaceHeatSerie,
      },
    ],
  }

  return result
}

export const calcVentilationHourSeries = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): HourSerieCalculatorResult => {
  let ventilationResult: HourSerieCalculatorResult = {
    outputs: [],
    inputs: [],
  }

  const ventilation = model.energySystems.find(
    (system) => system.type === EnergySystemType.Ventilation,
  )
  if (ventilation) {
    // Input from cooling system
    let coolingDemandDisplacementConstant = 0
    const cooling = model.energySystems.find(
      (system) => system.type === EnergySystemType.Cooling,
    )
    if (cooling)
      coolingDemandDisplacementConstant = (
        cooling.parameters as CoolingParameters
      ).acDemandDisplacementConstant

    ventilationResult = ventilationCalculator(
      model.schedules,
      outsideTempHourSerie,
      ventilation,
      coolingDemandDisplacementConstant,
      year,
    )
  }

  return ventilationResult
}
